import { useState } from 'react';
import { sendUserAttributeVerificationCode, confirmUserAttribute, fetchAuthSession } from 'aws-amplify/auth';

export type VerificationPhase = 'prompt' | 'sending' | 'entering' | 'confirming' | 'done';

/**
 * The emailed-code flow that proves an account's address. Used by VerifyEmailInline, on
 * the Account page (#1052) and in the Pricing page's verify alert (#1130).
 */
export function useEmailVerification(onVerified?: () => void) {
  const [phase, setPhase] = useState<VerificationPhase>('prompt');
  const [resending, setResending] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Resending from the code entry step must not tear the input out from under the
  // user, so a resend tracks its own flag instead of moving the phase machine.
  const send = async (isResend = false) => {
    setError(null);
    if (isResend) setResending(true); else setPhase('sending');
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
      setPhase('entering');
    } catch (e: any) {
      setError(e?.message || 'Could not send the code. Please try again.');
      if (!isResend) setPhase('prompt');
    } finally {
      if (isResend) setResending(false);
    }
  };

  const confirm = async () => {
    const trimmed = code.trim();
    // Guard the phase too: Enter fires this directly, bypassing the button's
    // disabled state, so without it a second keypress sends a second request.
    if (!trimmed || phase === 'confirming') return;
    setError(null);
    setPhase('confirming');
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: trimmed });
      // Cognito has the new attribute, but Amplify does not refresh tokens here, so
      // the cached ID token still carries email_verified:false — for up to its full
      // lifetime. Anything reading the claim (the subscribe gate, App on the next load)
      // would keep seeing an unverified account and send the user back to a
      // verification that has already been done. Force the refresh before declaring
      // success.
      try {
        await fetchAuthSession({ forceRefresh: true });
      } catch {
        /* Verification itself succeeded; a refresh failure must not read as one. */
      }
      setPhase('done');
      onVerified?.();
    } catch (e: any) {
      setError(e?.message || 'That code was not accepted. Please check it and try again.');
      setPhase('entering');
    }
  };

  return { phase, resending, code, setCode, error, send, confirm };
}
